import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  approveAdminQuotationOverride,
  listAdminQuotationExceptions,
  rejectAdminQuotation,
} from '../../supabaseService';
import { formatClientType } from '../../utils/clientTypeUtils';

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-IN');
  } catch (_err) {
    return value;
  }
}

function formatMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `₹${n.toLocaleString('en-IN')}`;
}

function billingCycleChipLabel(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'monthly') return '📅 Monthly';
  if (raw === 'quarterly') return '🗓 Quarterly';
  return raw ? String(value) : '—';
}

function billingBasisChipLabel(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'working_days_26') return '🏗 26 Working Days';
  return '📅 Calendar Month';
}

const CHIP_STYLE = {
  display: 'inline-block',
  background: 'rgba(201,168,76,0.12)',
  border: '1px solid rgba(201,168,76,0.5)',
  borderRadius: '8px',
  color: '#C9A84C',
  fontSize: '11px',
  fontWeight: 600,
  padding: '4px 10px',
};

const CHECK_LABELS = {
  check_kyc_ok: 'KYC not approved',
  check_compliance_ok: 'Machine compliance failed',
  check_rate_floor_ok: 'Rate below machine floor',
  check_availability_ok: 'Date overlap conflict',
  check_legal_fields_ok: 'Legal fields incomplete',
};

export default function QuotationExceptionsApprovals({ onQueueChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listAdminQuotationExceptions({ limit: 100 });
      const next = result?.items || [];
      setItems(next);
      if (onQueueChange) onQueueChange(next.length);
    } catch (err) {
      setItems([]);
      setError(err?.message || 'Failed to load quotation exceptions');
      if (onQueueChange) onQueueChange(0);
    } finally {
      setLoading(false);
    }
  }, [onQueueChange]);

  useEffect(() => {
    load();
  }, [load]);

  const handleApproveOverride = async (item) => {
    const note = window.prompt('Override note (required):');
    if (note == null) return;
    if (!String(note).trim()) {
      alert('Override note is required.');
      return;
    }
    setBusyId(item.id);
    try {
      const result = await approveAdminQuotationOverride(item.id, { adminNote: note.trim() });
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to approve override');
      }
      await load();
    } catch (err) {
      alert(err?.message || 'Failed to approve override');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (item) => {
    const note = window.prompt('Rejection note (required):');
    if (note == null) return;
    if (!String(note).trim()) {
      alert('Rejection note is required.');
      return;
    }
    setBusyId(item.id);
    try {
      const result = await rejectAdminQuotation(item.id, { adminNote: note.trim() });
      if (result?.success === false) {
        throw new Error(result.message || 'Failed to reject quotation');
      }
      await load();
    } catch (err) {
      alert(err?.message || 'Failed to reject quotation');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading quotation exceptions…</p>;
  if (error) {
    return (
      <div className="space-y-2">
        <p className="text-sm text-red-400">{error}</p>
        <Button type="button" size="sm" variant="outline" onClick={load}>
          Retry
        </Button>
      </div>
    );
  }
  if (items.length === 0) {
    return <p className="text-sm text-muted-foreground">No quotation exceptions pending admin action.</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((item) => {
        const reasons = Array.isArray(item?.checks?.adminQueueReasons)
          ? item.checks.adminQueueReasons
          : [];
        return (
          <div
            key={item.id}
            className="rounded-lg border border-[#C9A84C]/25 bg-[#C9A84C]/5 p-3 text-sm"
          >
            <div className="space-y-1">
              <p className="font-semibold text-[#E8D5A3]">
                {item.quotationRef || item.id} · {item.machine?.machineId || item.machine?.name || 'Machine'}
              </p>
              <p className="text-xs text-muted-foreground">
                Client: {item.client?.name || item.clientId || '—'} · {item.startDate || '—'} to {item.endDate || '—'}
              </p>
              <div className="flex flex-wrap gap-2 pt-1">
                <span style={CHIP_STYLE}>
                  {formatClientType(
                    item.clientType
                    || item.client_type
                    || item.client?.clientType
                    || item.client?.client_type,
                  )}
                </span>
                <span style={CHIP_STYLE}>
                  {billingCycleChipLabel(item.billingCycle || item.billing_cycle)}
                </span>
                <span style={CHIP_STYLE}>
                  {billingBasisChipLabel(item.billingBasis || item.billing_basis)}
                </span>
              </div>
              <p className="text-xs text-muted-foreground">
                Fixed rate: {formatMoney(item.fixedRatePerCycle)} · Floor: {formatMoney(item.machineFloor?.minimumRatePerCycle)}
              </p>
              <p className="text-xs text-muted-foreground">
                Entered admin review: {formatDate(item.adminReviewEnteredAt)} · Auto-checks run: {formatDate(item.checks?.autoChecksRunAt)}
              </p>
            </div>

            <div className="mt-2 flex flex-wrap gap-2">
              {reasons.length > 0 ? reasons.map((key) => (
                <span
                  key={`${item.id}-${key}`}
                  className="rounded-full border border-red-500/40 bg-red-500/10 px-2 py-0.5 text-[11px] text-red-300"
                >
                  {CHECK_LABELS[key] || key}
                </span>
              )) : (
                <span className="text-xs text-muted-foreground">No explicit failed-check reason recorded.</span>
              )}
            </div>

            <div className="mt-3 flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                disabled={busyId === item.id}
                onClick={() => handleApproveOverride(item)}
              >
                Approve override
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-red-500/40 text-red-300 hover:bg-red-500/10"
                disabled={busyId === item.id}
                onClick={() => handleReject(item)}
              >
                Reject
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
